import i18n from "../i18n";

// "Customise Table" — bridges the Table view's client-side fetch (no
// page reload, see TableTemplateControls.js's handleSaveTemplate/
// handleDeleteTemplate) to msb-api's nutrition-table-templates route.
// Unlike challenge-report-templates.js (submit-form + res.backDontScroll(),
// a full page redirect), this always responds with JSON — Nutrition
// Progress keeps a lot of state outside the URL (active tab, filters, Log's
// activeFilters) that a reload would wipe just to save a table template.
//
// Create vs. update share one POST route (Rill actions only support
// .post() — same reason challenge-report-templates.js's own create/update
// route branches on `id` instead of using a PUT verb) — presence of `id` in
// the body forwards to msb-api as a PUT instead of a POST. Delete is a
// second POST route, `/remove`, mirroring challenge-report-templates.js's
// own `/challenge-report-templates/remove`.

// Body arrives as a JSON string (Content-Type: application/json from the
// client fetch, not the multipart/form-encoded body a submit-form POST gets).
const parseJsonBody = (req) => {
  const { body } = req;
  if (typeof body !== "string") return body || {};
  try {
    return JSON.parse(body);
  } catch {
    return null;
  }
};

const failedMessage = (key, fallback) =>
  i18n.t(`athlete-portal:nutritionPage.progress.tableTemplate.errors.${key}`, fallback);

const NutritionTableTemplates = (app) => {
  app
    .post("/nutrition-table-templates", async ({ req, res, api, assert }) => {
      res.headers = res.headers || {};
      res.headers["Content-Type"] = "application/json; charset=utf-8";

      const body = parseJsonBody(req);
      if (body === null) {
        res.status = 400;
        res.body = JSON.stringify({ error: failedMessage("invalidBody", "Invalid request") });
        return;
      }

      const isEdit = Boolean(body.id);

      try {
        assert(
          body.name?.trim(),
          400,
          i18n.t(
            "athlete-portal:nutritionPage.progress.tableTemplate.errors.nameRequired",
            "Template name is required"
          )
        );
      } catch (e) {
        res.status = 400;
        res.body = JSON.stringify({ error: e.message });
        return;
      }

      const fallbackMessage = isEdit
        ? failedMessage("updateFailed", "Failed to update template")
        : failedMessage("saveFailed", "Failed to save template");

      try {
        const response = await api(
          isEdit ? `nutrition-table-templates/${body.id}` : "nutrition-table-templates",
          {
            method: isEdit ? "PUT" : "POST",
            body: JSON.stringify({ name: body.name, columns: body.columns || [] }),
            headers: { "Content-Type": "application/json" },
          }
        );

        if (!response.ok) {
          const text = await response.text();
          let errorPayload = { error: fallbackMessage };
          try {
            errorPayload = JSON.parse(text);
          } catch {
            if (text) errorPayload.error = text;
          }
          res.status = response.status;
          res.body = JSON.stringify(errorPayload);
          return;
        }

        const saved = await response.json().catch(() => ({}));
        res.status = response.status;
        res.body = JSON.stringify(saved);
      } catch (e) {
        res.status = 500;
        res.body = JSON.stringify({ error: e.message || fallbackMessage });
      }
    })

    .post("/nutrition-table-templates/remove", async ({ req, res, api, assert }) => {
      res.headers = res.headers || {};
      res.headers["Content-Type"] = "application/json; charset=utf-8";

      const body = parseJsonBody(req);
      if (body === null) {
        res.status = 400;
        res.body = JSON.stringify({ error: failedMessage("invalidBody", "Invalid request") });
        return;
      }

      try {
        assert(body.id, 400, failedMessage("deleteFailed", "Failed to delete template"));
      } catch (e) {
        res.status = 400;
        res.body = JSON.stringify({ error: e.message });
        return;
      }

      const deleteFailedMessage = failedMessage("deleteFailed", "Failed to delete template");

      try {
        const response = await api(`nutrition-table-templates/${body.id}`, {
          method: "DELETE",
        });

        if (!response.ok) {
          const text = await response.text();
          let errorPayload = { error: deleteFailedMessage };
          try {
            errorPayload = JSON.parse(text);
          } catch {
            if (text) errorPayload.error = text;
          }
          res.status = response.status;
          res.body = JSON.stringify(errorPayload);
          return;
        }

        const result = await response.json().catch(() => ({}));
        res.status = response.status;
        res.body = JSON.stringify(result);
      } catch (e) {
        res.status = 500;
        res.body = JSON.stringify({ error: e.message || deleteFailedMessage });
      }
    });
};

export default NutritionTableTemplates;
